import { PersistenceObjectType, PersistenceScope, SettingsManager, ViamapPersistenceLayer } from "@viamap/viamap2-common";
import { wfsEndpoints } from "src/dataCollector/WFSCollectorEndpoints";

const CUSTOM_GROUPS_FILENAME = "CutomFeatureLayersGroups.json";

const SOURCE_ALIASES: Record<string, string> = {
   Miljogis: "Miljøgis"
};

export type FavoriteConflictGroup = {
   uuid: string;
   title: string;
   layers: string[];
   layerSpecs?: Record<string, any>;
   favoriteConflictSearch?: boolean;
   online?: boolean;
};

export type FavoriteConflictSearchLayer = {
   id: string;
   groupId: string;
   groupTitle: string;
   source: keyof typeof wfsEndpoints;
   layer: string;
   title: string;
   filter?: any;
};

function formatComparisonFilter(filter: any): string | null {
   if (!Array.isArray(filter) || filter.length < 3 || typeof filter[0] !== "string") {
      return null;
   }
   const operator = filter[0] === "==" ? "=" : filter[0];
   const fieldPart = filter[1];
   const field = Array.isArray(fieldPart) ? fieldPart[1] : fieldPart;
   if (field === undefined || field === null || field === "") {
      return null;
   }
   return `"${field}" ${operator} ${filter[2]}`;
}

export function formatUserFilterExpressions(filter: any): string[] {
   if (!filter) {
      return [];
   }
   if (Array.isArray(filter) && (filter[0] === "all" || filter[0] === "any")) {
      return filter.slice(1).flatMap(formatUserFilterExpressions);
   }
   if (Array.isArray(filter) && Array.isArray(filter[0])) {
      return filter.flatMap(formatUserFilterExpressions);
   }
   const formatted = formatComparisonFilter(filter);
   return formatted ? [formatted] : [];
}

export function formatUserFilterNote(filter: any): string | null {
   const expressions = formatUserFilterExpressions(filter);
   return expressions.length > 0 ? expressions.join(", ") : null;
}

function isRecord(value: unknown): value is Record<string, any> {
   return typeof value === "object" && value !== null;
}

function resolveWfsSource(source: string): string {
   return SOURCE_ALIASES[source] ?? source;
}

function isWfsEndpoint(source: string): source is keyof typeof wfsEndpoints {
   return source in wfsEndpoints;
}

function readUserFilter(spec: any): any | undefined {
   const filter = spec?.mitValues?.userFilter
      ?? spec?.vectorHandle?.mitValues?.userFilter
      ?? spec?.layer?.mitValues?.userFilter;
   if (!filter || (Array.isArray(filter) && filter.length === 0)) {
      return undefined;
   }
   return filter;
}

function readWfsInfo(spec: any): { key: string; type: string } | null {
   const layer = spec?.layer ?? spec;
   const wfs = layer?.wfs ?? spec?.vectorHandle?.wfs;
   if (!wfs?.key || !wfs?.type) {
      return null;
   }
   const type = Array.isArray(wfs.type) ? wfs.type[0] : wfs.type;
   if (typeof type !== "string" || !type) {
      return null;
   }
   return { key: String(wfs.key), type };
}

function readLayerTitle(layerKey: string, spec: any): string {
   return spec?.layer?.label || spec?.vectorHandle?.label || spec?.label || layerKey;
}

export function extractSearchLayerFromFavoriteSpec(
   group: FavoriteConflictGroup,
   layerKey: string
): FavoriteConflictSearchLayer | null {
   const spec = group.layerSpecs?.[layerKey];
   const wfs = readWfsInfo(spec);
   if (!wfs) {
      return null;
   }
   const source = resolveWfsSource(wfs.key);
   if (!isWfsEndpoint(source)) {
      return null;
   }
   return {
      id: `${group.uuid}:${layerKey}`,
      groupId: group.uuid,
      groupTitle: group.title || layerKey,
      source,
      layer: wfs.type,
      title: readLayerTitle(layerKey, spec),
      filter: readUserFilter(spec)
   };
}

export function mapFavoriteGroupsToSearchLayers(groups: FavoriteConflictGroup[]): FavoriteConflictSearchLayer[] {
   const layers: FavoriteConflictSearchLayer[] = [];
   const seen = new Set<string>();

   for (const group of groups) {
      if (!group.favoriteConflictSearch) {
         continue;
      }
      for (const layerKey of group.layers ?? []) {
         const mapped = extractSearchLayerFromFavoriteSpec(group, layerKey);
         if (!mapped) {
            continue;
         }
         const dedupeKey = `${mapped.source}::${mapped.layer}::${JSON.stringify(mapped.filter ?? null)}`;
         if (seen.has(dedupeKey)) {
            continue;
         }
         seen.add(dedupeKey);
         layers.push(mapped);
      }
   }

   return layers;
}

function parsePersonalGroups(body: unknown): FavoriteConflictGroup[] {
   try {
      const parsed = JSON.parse(String(body));
      if (!Array.isArray(parsed)) {
         return [];
      }
      return parsed
         .filter((group) => isRecord(group) && typeof group.uuid === "string" && Array.isArray(group.layers))
         .map((group) => ({ ...group, online: false }) as FavoriteConflictGroup);
   } catch {
      return [];
   }
}

function parseOnlineGroup(body: unknown): FavoriteConflictGroup | null {
   try {
      const group = JSON.parse(String(body));
      if (!isRecord(group) || typeof group.uuid !== "string" || !Array.isArray(group.layers)) {
         return null;
      }
      return { ...group, online: true } as FavoriteConflictGroup;
   } catch {
      return null;
   }
}

export async function loadFavoriteConflictGroups(
   customerRef: string,
   userRef: string
): Promise<FavoriteConflictGroup[]> {
   const persist = new ViamapPersistenceLayer(SettingsManager.getSystemSetting("viamapStoreS3Bucket"));

   const personalGroups = await persist
      .getObject(PersistenceScope.User, PersistenceObjectType.CustomLayers, customerRef, userRef, CUSTOM_GROUPS_FILENAME)
      .then((result) => parsePersonalGroups(result.Body.toString("utf-8")))
      .catch(() => [] as FavoriteConflictGroup[]);

   const onlineGroups = await persist
      .getListing(PersistenceScope.Customer, PersistenceObjectType.CustomLayers, customerRef, userRef)
      .then(async (listing) => {
         const groups = await Promise.all(listing.map(async (item) => {
            return persist.getObjectS3(item.Key)
               .then((result) => parseOnlineGroup(result.Body.toString("utf-8")))
               .catch(() => null);
         }));
         return groups.filter((group): group is FavoriteConflictGroup => Boolean(group));
      })
      .catch(() => [] as FavoriteConflictGroup[]);

   return [...onlineGroups, ...personalGroups];
}
